import { Profile, Router, Storage } from "@lightningjs/sdk";

import { MediaType, UserModel } from "./models";
import { v4 as uuidv4 } from "uuid";
import configs from "./configs";
import { getUserData, getUserStatus } from "../api/queries";
import theme from "./theme";
import { setPinInputCallbacks } from "../widgets/PinInput/PinInput";

// Re-export setPinInputCallbacks for use in other modules
export { setPinInputCallbacks };

export const SPLASH_TIME = 2600;

export const SUBSCRIPTION_LINK = "pay.alteox.com";

export const MENU_WIDTH_WO_OFFSET = configs.layout?.menu.fullWidth ? theme.menu.activeW : theme.menu.w;
export const MENU_WIDTH = MENU_WIDTH_WO_OFFSET + theme.menu.offset;
export const MENU_WIDTH_ACTIVE = theme.menu.activeW + theme.menu.offset;

export interface PAGE {
    name: string;
    link: string;
}
export const PAGES = {
    home: {
        name: "home",
        link: "home"
    },
    hoteltv: {
        name: "hoteltv-welcome",
        link: "hoteltv-welcome"
    },
    live: {
        name: "live",
        link: "livetv"
    },
    browse: {
        name: "browse",
        link: "browse"
    },
    vod: {
        name: "vod",
        link: "vod"
    },
    movies: { name: "movies", link: "movies" },
    series: { name: "series", link: "series" },
    documentaries: { name: "documentaries", link: "documentaries" },
    search: {
        name: "search",
        link: "search"
    },
    favorite: {
        name: "favorite",
        link: "favorite"
    },
    info: {
        name: "info",
        link: "info"
    },
    settings: {
        name: "settings",
        link: "settings"
    },
    exit: {
        name: "exit",
        link: "exit"
    },
    apps: {
        name: "apps",
        link: "category/apps"
    }
};

export const PLATFORMS = {
    lg: "lg",
    samsung: "samsung",
    posttv: "posttv",
    metrological: "metrological",
    comcast: "comcast",
    nettv: "nettv",
    whaletv: "whaletv",
    philips: "philips",
    hisense: "hisense",
    androidtv: "androidtv",
    firetv: "firetv"
};

export const STORAGE_KEYS = {
    user: "user",
    platform: "platform",
    uid: "uid",
    token: "token",
    externalToken: "externalToken",
    selectedLanguage: "selectedLanguage",
    recentSearch: "recentSearch",
    lastMovieSelectedIndex: "lastMovieSelectedIndex",
    lastCategorySelectedIndex: "lastCategorySelectedIndex",
    parentalPin: "parentalPin",
    parentalAgeRestriction: "parentalAgeRestriction",
    pinFailedAttempts: "pinFailedAttempts",
    pinLockoutUntil: "pinLockoutUntil",
    parentalReminderAt: "parentalReminderAt",
    audioLang: "audioLang",
    subLang: "subLang",
    preferredAudio: "preferredAudio",
    preferredSubtitle: "preferredSubtitle",
    posttvOS: "posttvOS",
    /**
     * Netgem/PostTV STB provides a signed websocket signature in URL hash.
     * We capture it early (before router hash navigation overwrites it).
     */
    posttvWsSignature: "posttvWsSignature",
    otp: "otp",
    vodafoneWatchTime: "vodafoneWatchTime"
};

export const getHomePage = () => {
    // todo: to check default page is enabled
    const defaultPage = configs.defaultPage || "vod";
    return defaultPage === "livetv" && !isSubscriptionActive() ? "hoteltv-login" : defaultPage;
};

export const getPlatform = async () => {
    return Storage.get(STORAGE_KEYS.platform) || (await Profile.platform());
};

const DEVICE_ID_TIMEOUT_MS = 3000;

export const deviceId = async () => {
    let uid = Storage.get(STORAGE_KEYS.uid);

    if (uid) return uid;

    // Profile.uid() bridges to a platform-native API on some devices and can hang
    // indefinitely with no timeout of its own if that bridge never responds.
    uid = await Promise.race([
        Profile.uid(),
        new Promise<string>((resolve) => {
            setTimeout(() => {
                console.log(`Profile.uid() did not resolve within ${DEVICE_ID_TIMEOUT_MS}ms, using generated id`);
                resolve(uuidv4());
            }, DEVICE_ID_TIMEOUT_MS);
        })
    ]);

    // local browser
    if (uid === "ee6723b8-7ab3-462c-8d93-dbf61227998e") {
        uid = uuidv4();
    }

    Storage.set(STORAGE_KEYS.uid, uid);

    return uid;
};

export const getUserId = () => {
    return Storage.get(STORAGE_KEYS.user)?.id || "";
};

export const formatDuration = (progress: number, duration: number, useSeconds?: boolean) => {
    if (!progress || !duration) return "";

    progress = new Date(0, 0, 0, 0, 0, 0, 0).setSeconds(progress);
    duration = new Date(0, 0, 0, 0, 0, 0, 0).setSeconds(duration);

    return `${new Date(progress).toLocaleString("en-GB", {
        hour: "numeric",
        minute: "numeric",
        second: "numeric"
    })} - ${new Date(duration).toLocaleString("en-GB", {
        hour: "numeric",
        minute: "numeric",
        second: "numeric"
    })}`;
};

interface FormatTimeProps {
    useSeconds: boolean;
    hideEmpty: boolean;
}

export const formatTime = (
    value: number | undefined,
    { useSeconds, hideEmpty }: FormatTimeProps = {
        useSeconds: true,
        hideEmpty: true
    }
) => {
    if (!value && hideEmpty) return "";

    const totalMinutes = value ? Math.floor(value / 60) : 0;

    const seconds = value ? Math.floor(value % 60) : 0;
    const hours = Math.floor(totalMinutes / 60);
    const minutes = Math.floor(totalMinutes % 60);

    return hideEmpty && hours === 0 && minutes === 0 && seconds === 0
        ? ""
        : hours > 0
          ? `${hours}h ${minutes > 0 ? `${minutes}m` : ""}`
          : `${minutes}m ${useSeconds ? `${seconds}s` : ""}`;
};

export const storageSaveUser = (data: UserModel | undefined) => {
    if (data) {
        const { token, ...userData } = data;
        Storage.set(STORAGE_KEYS.user, userData);

        if (token) {
            Storage.set(STORAGE_KEYS.token, token);
        }
    } else {
        Storage.remove(STORAGE_KEYS.user);
    }
};

export const getInfoFromTitle = (title: string) => {
    title = title || "";

    const separator = title.lastIndexOf("-") === -1 ? ":" : "-";

    return {
        title: title.substring(0, title.lastIndexOf(separator)) || title,
        category: title.split(separator).pop() || ""
    };
};

export const isExternalPlatform = () => {
    return (
        Storage.get(STORAGE_KEYS.platform) === PLATFORMS.androidtv ||
        Storage.get(STORAGE_KEYS.platform) === PLATFORMS.firetv
    );
};

export const isIAPSupported = () => {
    return (isExternalPlatform() && configs.iap) || Storage.get(STORAGE_KEYS.platform) === PLATFORMS.firetv;
};

export const isPostTvPlatform = () => {
    return Storage.get(STORAGE_KEYS.platform) === PLATFORMS.posttv;
};

export const isPostTvAndroid = () => {
    return isPostTvPlatform() && Storage.get(STORAGE_KEYS.posttvOS) === "android";
};

export const isPostTvLinux = () => {
    return isPostTvPlatform() && Storage.get(STORAGE_KEYS.posttvOS) === "linux";
};

/**
 * True when the box has a parental PIN we can act on. The PIN lives on the BOX,
 * never in our backend — we deliberately do NOT read user.settings.parentalPin here.
 * - Android: the box only exposes a validator (window.Android.testPinCode); its
 *   presence means a PIN exists and is checked at entry time (we never read the value).
 * - Linux: the box PIN value is synced into Storage from pics.pin over the STB
 *   websocket at boot.
 */
export const hasBoxPin = (): boolean => {
    if (isPostTvAndroid()) {
        const android: any = (window as any).Android;
        return !!(android && typeof android.testPinCode === "function");
    }
    if (isPostTvLinux()) {
        return !!Storage.get(STORAGE_KEYS.parentalPin);
    }
    return false;
};

export const isSubscriptionActive = () => {
    const user = Storage.get(STORAGE_KEYS.user);
    return user?.subscribed;
    // return user && user.currentPeriodEnd && user.currentPeriodEnd > Date.now() / 1000;
};

export const navigateToPlayback = (id: any) => {
    Router.navigate(`playback/${id}`, {
        keepAlive: Router.getActiveHash() === "search"
    });
};

export const getMediaRedirect = async (
    id: any,
    {
        isFree,
        movie
    }: {
        isFree: boolean;
        movie?: any;
    }
) => {
    // console.log("isSubscriptionActive()", isSubscriptionActive());
    // console.log("isExternalPlatform()", isExternalPlatform());
    // console.log("isIAPSupported()", isIAPSupported());
    // console.log('movie', movie)

    // Check if parental PIN is required for adult content
    // const useParentalPin = configs.useParentalPin === true;
    const isSubscribed = isSubscriptionActive();
    // const isAdultContent = movie && (
    //     (movie.fsk && movie.fsk >= 6) ||
    //     movie.isAdult === true
    // );

    // if (useParentalPin && isSubscribed && isAdultContent && isPostTvAndroid()) {
    //     // Show PIN input widget - set callbacks and focus widget
    //     setPinInputCallbacks(
    //         () => navigateToPlayback(id),
    //         () => {}
    //     );
    //     Router.focusWidget("PinInput");
    //     return;
    // }

    if (isFree || isSubscribed) {
        navigateToPlayback(id);
    } else {
        if (isIAPSupported()) {
            const user = Storage.get(STORAGE_KEYS.user);
            if (!user?.isLoggedIn) {
                Router.focusWidget("IAPSubscription");
            } else {
                // try to sync first
                try {
                    console.log("TRY SYNC");
                    await window.setSubscriptionData(window.Android!.getSubscriptionData(), true);
                } catch (e) {
                    console.log("SYC ERR");
                    console.log(e);
                }

                console.log("after sync: isActive  " + isSubscriptionActive());
                if (!isSubscriptionActive()) {
                    console.log("call createSubscription");
                    Router.focusWidget("IAPSubscription");
                } else {
                    navigateToPlayback(id);
                }
            }
        } else if (isPostTvPlatform()) {
            Router.focusWidget("PostTvSubscription");
        } else {
            Router.focusWidget("Auth");
        }
    }
};

/**
 * Waits for user initialization to complete.
 * User initialization is complete when:
 * - Token exists (set during refreshUserData)
 * - User object exists (stored after getUserData/getUserStatus)
 *
 * Times out after 10 seconds to prevent infinite waiting.
 * After timeout, proceeds anyway (backend will handle gracefully).
 */
export const waitForUserInitialization = async (): Promise<boolean> => {
    const maxWaitTime = 10000; // 10 seconds
    const checkInterval = 100; // Check every 100ms
    const startTime = Date.now();

    while (Date.now() - startTime < maxWaitTime) {
        const user = Storage.get(STORAGE_KEYS.user);
        const token = Storage.get(STORAGE_KEYS.token);

        // User initialization is complete when:
        // 1. Token exists (set in refreshUserData after getUserData/getUserStatus)
        // 2. User object exists (stored in storageSaveUser)
        // The token is the key indicator that initialization has completed
        if (token) {
            // Token exists means initialization completed
            // Even if user object isn't set yet, the backend will handle it
            return true;
        }

        await new Promise((resolve) => setTimeout(resolve, checkInterval));
    }

    // Timeout reached, but we'll still try to proceed
    // The backend will handle missing user gracefully with the fallback country logic
    console.warn("User initialization timeout - proceeding anyway");
    return false;
};

export const redirectToSelectedMovie = async ({
    id,
    type,
    isFree,
    movie
}: {
    id: number | string;
    type: MediaType;
    isFree: boolean;
    movie: any;
}) => {
    // Wait for user initialization before navigating
    // This prevents the "Cannot read properties of undefined (reading 'settings')" error
    await waitForUserInitialization();

    if (type === "ExternalApp") {
        if (window.Android?.navigateToApp) {
            window.Android?.navigateToApp(id as string);
        }
    } else if (type === "Episode" || type === "LiveStream") {
        getMediaRedirect(id, {
            isFree: isFree,
            movie: movie
        });
    } else if (type === "Movie") {
        Router.navigate(`movie/${id}`, {
            keepAlive: true
        });
    } else if (type === "ChannelContent") {
        window.selectedChannel = {
            id: movie.id,
            name: movie.title,
            url: movie.url || "",
            logo: movie.logo || ""
        };

        Router.navigate("livetv", {
            keepAlive: true
        });
    } else {
        window.currentSeason = movie.currentSeasonNumber;
        Router.navigate(`series/${id}`, {
            keepAlive: true
        });
    }
};

export const refreshUserData = async (isOnStart?: boolean) => {
    try {
        // save user obj
        //const { token, ...userData } = isOnStart ? await getUserData() : await getUserStatus();

        const userResult = isOnStart ? await getUserData() : await getUserStatus();

        if (!userResult) {
            return;
        }

        const { token, ...userData } = userResult;

        await storageSaveUser(userData);

        // non posttv
        // refresh auth token
        // if (!isPostTvPlatform() && token) {
        if (token) {
            Storage.set(STORAGE_KEYS.token, token);
        }

        if (isIAPSupported()) {
            // call getPurchaseToken for google, user id is not available there.
            // might be as well empty
            // todo: show 'pls login screen'
            const userId =
                Storage.get(STORAGE_KEYS.platform) === PLATFORMS.androidtv
                    ? window.Android!.getPurchaseToken()
                    : window.Android!.getUserId()!;

            // Merge IAP data with existing user data instead of replacing it
            const currentUser = Storage.get(STORAGE_KEYS.user) || {};

            await storageSaveUser({
                ...currentUser,
                userId,
                // currentPeriodEnd: null,
                subscribed: false
            });
            try {
                console.log("TRY SYNC");
                await window.setSubscriptionData(window.Android!.getSubscriptionData(), true);
            } catch (e) {
                console.log("SYC ERR");
                console.log(e);
            }
        }
    } catch (e) {
        //await storageSaveUser(undefined);
        console.error("refreshUserData error:", e);
    }
};

export const isSubscriptionAvailable = () => {
    // return !isPostTvPlatform();
    return true;
};

export const getMediaPosterProps = (type?: MediaType) => {
    const isChannel = type === "ChannelContent" || type === "LiveTv";
    const isEpisode = type === "Episode";
    const isExternalApp = type === "ExternalApp";

    // const scale = Router.getActiveHash()?.startsWith('category/') ? 1.2 : 1;
    const scale = 1;

    return {
        w:
            (isExternalApp
                ? theme.externalAppItem.w
                : isChannel
                  ? theme.channelItem.bg.w
                  : isEpisode
                    ? theme.episodeItem.w
                    : theme.mediaItem.w) * scale,
        h: isExternalApp
            ? theme.externalAppItem.h
            : isChannel
              ? theme.channelItem.bg.h
              : isEpisode
                ? theme.episodeItem.h
                : theme.mediaItem.h * scale,
        posterHeight: isExternalApp
            ? theme.externalAppItem.img.h
            : isChannel
              ? theme.channelItem.logo.h
              : isEpisode
                ? theme.episodeItem.h
                : theme.mediaItem.h * scale,
        posterWidth: isExternalApp
            ? theme.externalAppItem.img.w
            : isChannel
              ? theme.channelItem.logo.w
              : isEpisode
                ? theme.episodeItem.w
                : theme.mediaItem.w * scale,
        radius: isExternalApp
            ? theme.externalAppItem.border.radius
            : isChannel
              ? theme.channelItem.bg.border.radius
              : theme.mediaItem.border.radius * scale,
        borderW: isExternalApp ? theme.externalAppItem.border.w : theme.mediaItem.border.w,
        isChannel,
        isEpisode,
        isExternalApp
    };
};

export const getCountryName = (code: string) => {
    if (typeof Intl == "undefined" || typeof Intl.DisplayNames == "undefined") {
        return code;
    }

    const names = new Intl.DisplayNames([Storage.get(STORAGE_KEYS.selectedLanguage)], {
        type: "region"
    });
    return names.of(code.toUpperCase());
};

export const getLanguageName = (code: string) => {
    if (typeof Intl == "undefined" || typeof Intl.DisplayNames == "undefined") {
        return code;
    }

    const names = new Intl.DisplayNames([Storage.get(STORAGE_KEYS.selectedLanguage)], {
        type: "language"
    });
    return names.of(code.toUpperCase());
};
